import type {
  Antibiotic,
  IPatientHistory,
  IPatientRequestDetail,
  IPatientRequestMaterial,
  IPatientRequestTest,
} from './defs';

const COL_GAP = 2;
const MIN_TECH_W = 10;
const MAX_TECH_W = 44;
const MAX_UNITS_W = 14;
const MAX_RANGES_W = 20;
const MIN_RESULT_W = 24;
const TARGET_LINE_W = 96;

export type RequestCopyLabels = {
  disclaimer1: string;
  disclaimer2: string;
  disclaimer3: string;
  colTechnique: string;
  colResults: string;
  colUnits: string;
  colRanges: string;
  colAntibiotic: string;
  colInterpretation: string;
  labelRequest: string;
  labelDate: string;
  labelNhc: string;
};

type TableRow =
  | { kind: 'data'; tech: string; result: string; units: string; ranges: string }
  | { kind: 'comment'; result: string };

/** Quita HTML/XML de fragmentos que vienen del API; salida solo texto. */
export function stripHtml(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '';
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function isHeaderRow(test: IPatientRequestTest): boolean {
  return !test.mikrotyp && [10, 12, 13].includes(test.restyp);
}

export function isCommentRow(
  test: IPatientRequestTest,
  tests: IPatientRequestTest[],
  index: number
): boolean {
  if (index <= 0) return false;
  return test.metname === tests[index - 1].metname;
}

function isDataRow(
  test: IPatientRequestTest,
  tests: IPatientRequestTest[],
  index: number
): boolean {
  return !isHeaderRow(test) && !isCommentRow(test, tests, index);
}

function padCell(s: string, width: number, tail = ''): string {
  const t = stripHtml(s);
  if (t.length <= width) return t.padEnd(width, ' ');
  if (width <= tail.length) return tail.slice(0, width);
  return t.slice(0, width - tail.length) + tail;
}

/** Parte texto en líneas de como mucho width caracteres (por palabras). */
export function wrapPlainText(text: string, width: number): string[] {
  const clean = stripHtml(text).replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
  if (!clean) return [''];
  if (width < 8) return [clean.slice(0, width)];
  const lines: string[] = [];
  let rest = clean;
  while (rest.length > width) {
    let cut = rest.lastIndexOf(' ', width);
    if (cut <= 0) cut = width;
    lines.push(rest.slice(0, cut).trimEnd());
    rest = rest.slice(cut).trimStart();
  }
  if (rest) lines.push(rest);
  return lines;
}

function collectTableRows(
  tests: IPatientRequestTest[]
): Array<{ sectionTitle: string | null; rows: TableRow[] }> {
  const segments: Array<{ sectionTitle: string | null; rows: TableRow[] }> = [];
  let current: TableRow[] = [];
  let pendingTitle: string | null = null;

  const flush = () => {
    if (current.length > 0) {
      segments.push({ sectionTitle: pendingTitle, rows: current });
      current = [];
      pendingTitle = null;
    }
  };

  for (let i = 0; i < tests.length; i++) {
    const t = tests[i];
    if (isHeaderRow(t)) {
      flush();
      pendingTitle = stripHtml(t.metname);
      continue;
    }
    if (isCommentRow(t, tests, i)) {
      current.push({ kind: 'comment', result: t.resultado ?? '' });
      continue;
    }
    if (isDataRow(t, tests, i)) {
      current.push({
        kind: 'data',
        tech: t.metname ?? '',
        result: t.resultado ?? '',
        units: t.unidades ?? '',
        ranges: t.rangos ?? '',
      });
    }
  }
  flush();

  if (segments.length === 0 && tests.some((t, i) => isDataRow(t, tests, i))) {
    const rows: TableRow[] = [];
    for (let i = 0; i < tests.length; i++) {
      const t = tests[i];
      if (isHeaderRow(t)) continue;
      if (isCommentRow(t, tests, i)) {
        rows.push({ kind: 'comment', result: t.resultado ?? '' });
      } else if (isDataRow(t, tests, i)) {
        rows.push({
          kind: 'data',
          tech: t.metname ?? '',
          result: t.resultado ?? '',
          units: t.unidades ?? '',
          ranges: t.rangos ?? '',
        });
      }
    }
    if (rows.length) segments.push({ sectionTitle: null, rows });
  }

  return segments;
}

function computeWidths(
  rows: TableRow[],
  labels: RequestCopyLabels
): { wT: number; wR: number; wU: number; wRg: number } {
  const dataRows = rows.filter((r): r is Extract<TableRow, { kind: 'data' }> => r.kind === 'data');
  let wT = Math.max(
    MIN_TECH_W,
    labels.colTechnique.length,
    ...dataRows.map((r) => stripHtml(r.tech).length)
  );
  wT = Math.min(wT, MAX_TECH_W);

  let wU = Math.max(
    3,
    labels.colUnits.length,
    ...dataRows.map((r) => stripHtml(r.units).length)
  );
  wU = Math.min(wU, MAX_UNITS_W);

  let wRg = Math.max(
    3,
    labels.colRanges.length,
    ...dataRows.map((r) => stripHtml(r.ranges).length)
  );
  wRg = Math.min(wRg, MAX_RANGES_W);
  const gaps = 3 * COL_GAP;
  let wR = TARGET_LINE_W - wT - wU - wRg - gaps;
  while (wR < MIN_RESULT_W && wT > MIN_TECH_W) {
    wT -= 1;
    wR = TARGET_LINE_W - wT - wU - wRg - gaps;
  }
  wR = Math.max(MIN_RESULT_W, wR);
  return { wT, wR, wU, wRg };
}

function formatTableBlock(rows: TableRow[], labels: RequestCopyLabels): string[] {
  if (rows.length === 0) return [];
  const { wT, wR, wU, wRg } = computeWidths(rows, labels);
  const gap = ' '.repeat(COL_GAP);
  const sep = `${'-'.repeat(wT)}${gap}${'-'.repeat(wR)}${gap}${'-'.repeat(wU)}${gap}${'-'.repeat(wRg)}`;
  const head = `${padCell(labels.colTechnique, wT)}${gap}${padCell(labels.colResults, wR)}${gap}${padCell(labels.colUnits, wU)}${gap}${padCell(labels.colRanges, wRg)}`;
  const out: string[] = [head, sep];

  const indentComment = wT + gap.length;

  for (const row of rows) {
    if (row.kind === 'comment') {
      const lines = wrapPlainText(row.result, wR);
      for (let li = 0; li < lines.length; li++) {
        out.push(`${''.padEnd(indentComment)}${padCell(lines[li] ?? '', wR)}`);
      }
      continue;
    }
    const tech = padCell(row.tech, wT);
    const units = padCell(row.units, wU);
    const ranges = padCell(row.ranges, wRg);
    const resLines = wrapPlainText(row.result, wR);
    for (let li = 0; li < resLines.length; li++) {
      const rawLine = resLines[li] ?? '';
      const resCell =
        rawLine.length <= wR ? rawLine.padEnd(wR, ' ') : padCell(rawLine, wR, '…');
      if (li === 0) {
        out.push(`${tech}${gap}${resCell}${gap}${units}${gap}${ranges}`);
      } else {
        out.push(`${''.padEnd(wT, ' ')}${gap}${resCell}${gap}${''.padEnd(wU, ' ')}${gap}${''.padEnd(wRg, ' ')}`);
      }
    }
  }
  return out;
}

function formatAntibioticBlock(
  antibiotics: Antibiotic[],
  labels: RequestCopyLabels
): string[] {
  if (!antibiotics?.length) return [];
  const bacteriumNames = getAntibioticColumnHeaders(antibiotics, labels);
  const rows = antibiotics.map((a) => ({
    name: stripHtml(a.methname ?? ''),
    values: bacteriumNames.map((_, index) => getAntibioticColumnValue(a, index)),
  }));
  const gap = ' '.repeat(COL_GAP);
  const widths = computeAntibioticWidths(rows, bacteriumNames, labels);
  const lines = [
    ...formatAntibioticRow([labels.colAntibiotic, ...bacteriumNames], widths, gap),
    widths.map((w) => '-'.repeat(w)).join(gap),
  ];
  for (const r of rows) {
    lines.push(...formatAntibioticRow([r.name, ...r.values], widths, gap));
  }
  return lines;
}

function getAntibioticColumnHeaders(
  antibiotics: Antibiotic[],
  labels: RequestCopyLabels
): string[] {
  const rawNames = stripHtml(antibiotics[0]?.[''] ?? '');
  const bacteriumNames = rawNames
    .replace(/[^a-zA-Z0-9 #]/g, '')
    .split('#')
    .map((name) => name.trim())
    .filter(Boolean);
  return bacteriumNames.length ? bacteriumNames : [labels.colInterpretation];
}

function getAntibioticColumnValue(antibiotic: Antibiotic, columnIndex: number): string {
  return stripHtml(antibiotic[`columna${columnIndex + 1}`] ?? '');
}

function computeAntibioticWidths(
  rows: Array<{ name: string; values: string[] }>,
  headers: string[],
  labels: RequestCopyLabels
): number[] {
  const valueColumnCount = Math.max(headers.length, 1);
  const maxNameWidth = Math.max(
    labels.colAntibiotic.length,
    ...rows.map((r) => r.name.length),
    12
  );
  const totalGapWidth = valueColumnCount * COL_GAP;
  const availableWidth = TARGET_LINE_W - totalGapWidth;
  let nameWidth = Math.min(Math.max(maxNameWidth, 12), 28);
  const minValueWidth = valueColumnCount > 2 ? 8 : 12;
  let valueWidth = Math.floor((availableWidth - nameWidth) / valueColumnCount);

  if (valueWidth < minValueWidth) {
    valueWidth = minValueWidth;
    nameWidth = Math.max(10, availableWidth - valueWidth * valueColumnCount);
  }

  const valueWidths = headers.map((header, index) => {
    const preferredWidth = Math.max(
      stripHtml(header).length,
      ...rows.map((r) => r.values[index]?.length ?? 0),
      minValueWidth
    );
    return Math.min(Math.max(preferredWidth, minValueWidth), valueWidth);
  });

  return [nameWidth, ...valueWidths];
}

function formatAntibioticRow(
  cells: string[],
  widths: number[],
  gap: string
): string[] {
  const wrapped = cells.map((cell, index) =>
    wrapPlainText(cell, widths[index] ?? MIN_RESULT_W)
  );
  const maxLines = Math.max(...wrapped.map((cellLines) => cellLines.length), 1);
  const lines: string[] = [];

  for (let lineIndex = 0; lineIndex < maxLines; lineIndex++) {
    lines.push(
      widths
        .map((width, cellIndex) => padCell(wrapped[cellIndex]?.[lineIndex] ?? '', width))
        .join(gap)
    );
  }

  return lines;
}

function majorDivider(width: number): string {
  return ''.padEnd(Math.min(width, TARGET_LINE_W), '═');
}

function minorDivider(width: number): string {
  return ''.padEnd(Math.min(width, TARGET_LINE_W), '─');
}

function formatTestsSections(tests: IPatientRequestTest[], labels: RequestCopyLabels): string[] {
  const segments = collectTableRows(tests);
  const lines: string[] = [];
  for (const seg of segments) {
    if (lines.length) lines.push('');
    if (seg.sectionTitle) {
      lines.push(seg.sectionTitle);
      lines.push(minorDivider(Math.min(TARGET_LINE_W, stripHtml(seg.sectionTitle).length + 8)));
      lines.push('');
    }
    lines.push(...formatTableBlock(seg.rows, labels));
  }
  return lines;
}

function formatMaterialBlock(
  mat: IPatientRequestMaterial,
  labels: RequestCopyLabels
): string[] {
  const lines: string[] = [
    '',
    majorDivider(TARGET_LINE_W),
    stripHtml(mat.methname ?? ''),
    minorDivider(TARGET_LINE_W),
    '',
  ];
  lines.push(...formatTestsSections(mat.tests ?? [], labels));
  const ab = formatAntibioticBlock(mat.antibioticos ?? [], labels);
  if (ab.length) {
    lines.push('');
    lines.push(...ab);
  }
  return lines;
}

export function formatRequestDateForCopy(einDat: string | null | undefined): string {
  if (einDat == null || einDat === '') return '';
  // Parseo explícito de componentes para evitar que new Date() interprete
  // ambiguamente el formato (p.ej. DD/MM/YYYY leído como MM/DD/YYYY).
  // Soporta: YYYY-MM-DD HH:mm[:ss], YYYY-MM-DDTHH:mm[:ss], DD/MM/YYYY HH:mm[:ss]
  const isoMatch = einDat.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
  const euMatch = !isoMatch && einDat.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})[T\s](\d{2}):(\d{2})/);
  let d: Date;
  if (isoMatch) {
    d = new Date(+isoMatch[1], +isoMatch[2] - 1, +isoMatch[3], +isoMatch[4], +isoMatch[5]);
  } else if (euMatch) {
    d = new Date(+euMatch[3], +euMatch[2] - 1, +euMatch[1], +euMatch[4], +euMatch[5]);
  } else {
    return einDat;
  }
  if (Number.isNaN(d.getTime())) return einDat;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

export function buildRequestCopyPlainText(
  detail: IPatientRequestDetail,
  patient: IPatientHistory | null,
  labels: RequestCopyLabels
): string {
  const lines: string[] = [];
  lines.push(labels.disclaimer1, '', labels.disclaimer2, '', labels.disclaimer3);
  lines.push('', majorDivider(TARGET_LINE_W), '');

  const pet = String(detail.peticion ?? detail.auftrNr ?? '').trim();
  const fe = formatRequestDateForCopy(detail.einDat);
  const nhc = stripHtml(patient?.historia ?? '');

  lines.push(
    `${labels.labelRequest.padEnd(12, ' ')} ${pet}`,
    `${labels.labelDate.padEnd(12, ' ')} '${fe}'`,
    `${labels.labelNhc.padEnd(12, ' ')} ${nhc}`
  );
  lines.push('', majorDivider(TARGET_LINE_W), '');

  if (detail.tests?.length) {
    lines.push(...formatTestsSections(detail.tests, labels));
  }

  for (const mat of detail.materiales ?? []) {
    lines.push(...formatMaterialBlock(mat, labels));
  }

  return lines.join('\n').replace(/\n{4,}/g, '\n\n\n').trimEnd() + '\n';
}
